/*istanbul ignore file*/
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import get from 'lodash/get';
import { useQuery } from '@apollo/client';
import Button from '@atlaskit/button';
import ArrowLeftCircleIcon from '@atlaskit/icon/glyph/arrow-left-circle';
import { N0, N800A } from '@atlaskit/theme/colors';
import LoadingButton from '@atlaskit/button/loading-button';
import ToolTip from '@atlaskit/tooltip';
import {
  AnalyticsButtonLabels,
  AnalyticsEventSource,
  AnalyticsTrackAction,
  AnalyticsTrackSubject,
  sendAnalyticsEvent,
  trackButtonClick,
} from '@atlassian/temp-analytics';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { updateDefaultPaymentMethod, updateInvoiceGroup, getInvoiceGroupCount } from '@atlassian/temp-proxies';
import { removeContentScrollingHack } from '@atlassian/cssxp-utils-generic';
import { UpdateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';
import {
  AppContent,
  AppPage,
  ErrorComponents,
  errorNotificationBuilder,
  MoreActionsItems,
  MoreActionsV2,
  NotificationTypes,
  OpenSidebarPayload,
  PageTabs,
  useFeatureToggle,
  useNotification,
  useSidebar,
  withApollo,
  useRecentlyViewed,
  CopyableText,
} from '@css-xp/base';
//@ts-ignore
import PPCHeader from '@css-xp/base/assets/img/ppc-header.png';
import { useApplicationContext } from '@css-xp/base/common-context';
import { ManagePaymentMethodsSidebar, SetCoBillDate } from '@css-xp/base/common-sidebars';
import {
  ContactInfo,
  FetchInvoiceGroupPpcQuery,
  FetchInvoiceGroupPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  PPC_PAGE_TITLE,
  ENABLE_DESIGN_ITERATION_1,
  routePaths,
  ADDITIONAL_INVOICE_RECIPIENTS,
  SUBSCRIPTION_CONSTANTS,
  refundedTabNameKey,
  RecentlyViewedEntityEnum,
  STATSIG_FEATURE_GATES,
  COMMERCIAL_PRICE_ELIGIBILITY,
  INVOICE_CONTACTS,
} from '@css-xp/ppc-constants';
import {
  BillingAdminEdge,
  FetchBillingAdminsForTransactionAccountPpcQuery,
  FetchBillingAdminsForTransactionAccountPpcQueryVariables,
} from '@css-xp/ppc-graphtypes';
import { InvoiceTable, InvoiceTableColumns } from '@css-xp/ppc-invoice-table';
import { IncludedProductsSidebar, SubscriptionTableFlatContainer } from '@css-xp/ppc-subscription-table';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { DEFAULT_INFLIGHT_BANNER_MESSAGE, failureMessage, ScreenEventAnalytics } from '@css-xp/ppc-common';
import { useGetRelatedDomainsForCurrentTxA, useTitle } from '@css-xp/ppc-custom-hooks';
import { OrderHistoryTable } from '@css-xp/ppc-order-history-table';
import { QuoteTableContainer, defaultQuotesFilterOptions } from '@css-xp/ppc-quote-table';
import { CreateBillingProfilePane } from '@css-xp/ppc-billing-profile-table';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { PageBackgroundV2 } from '@css-xp/page-background';
import {
  getCompanyNameRoute,
  getEntityName,
  getIsBillingProfileUsedForInternalUsageLicense,
  shouldShowACHPaymentMethod,
} from '@css-xp/ppc-utils';
import { LayoutAppearance } from '@css-xp/ppc-types';
import { hiddenTabsQuoteFilterOptions } from '@css-xp/ppc-quote-table/src/quote-table';
import { CustomerContactsTab } from '@css-xp/ppc-entitlement-page';
import { BillingProfileDetailsPageLoader } from '@css-xp/ppc-page-loaders';
import { ShipToPartySidebar } from '@css-xp/ppc-ship-to-party-sidebar';
import { ContactType, useFetchEccFromContactService } from '@css-xp/contact-resources';
import { fetchBillingAdminsForTransactionAccountPPC } from '../../../../queries';
import { BillingProfileSummary } from './components/billing-profile-summary';
import { InvoiceContactsTab } from './components/invoice-contacts';
import { EditInvoiceContactPane } from './components/sidebar/invoice-contact-pane';
import { EditBillingProfilePane } from './components/sidebar/edit-billing-profile-pane';
import { EditBillingProfileNamePane } from './components/sidebar/edit-billing-profile-name-pane';
import { CustomerContactPane } from './components/sidebar/customer-contact-pane';
import { DeactivateConfirmModal, DeactivateErrorModal } from './components/deactivate-billing-profile';
import { fetchInvoiceGroupQueryPPC } from '../queries/fetch-invoice-group-ppc';
import { BillingAdminPane } from './components/sidebar/billing-admin-pane';
import { BillingProfileLozenge } from './components/billing-profile-lozenge';
import './billing-profile-details-page.scss';

export enum SIDEBAR {
  EDIT_BILLING_PROFILE = 'edit-billing-profile-perf-v2',
  EDIT_BILLING_PROFILE_NAME = 'edit-billing-profile-name',
  MANAGE_PAYMENT_METHODS = 'manage-payment-methods',
  UPDATE_INVOICE_RECIPIENTS = 'update-invoice-recipients',
  SET_BILL_DATE = 'set-bill-date',
  CUSTOMER_CONTACT_PANEL = 'customerContactPane',
  CREATE_BILLING_PROFILE = 'createBillingProfilePane',
  EDIT_BILLING_PROFILE_ADDRESS = 'edit-billing-profile-address',
  BILLING_ADMIN_PANEL = 'billingAdminPanel',
}

const allowedSidebars = {
  [SIDEBAR.EDIT_BILLING_PROFILE]: EditBillingProfilePane,
  [SIDEBAR.MANAGE_PAYMENT_METHODS]: ManagePaymentMethodsSidebar,
  [SIDEBAR.SET_BILL_DATE]: withApollo(SetCoBillDate),
  [SIDEBAR.UPDATE_INVOICE_RECIPIENTS]: EditInvoiceContactPane,
  [SIDEBAR.CREATE_BILLING_PROFILE]: CreateBillingProfilePane,
  [SIDEBAR.EDIT_BILLING_PROFILE_NAME]: EditBillingProfileNamePane,
  [SIDEBAR.CUSTOMER_CONTACT_PANEL]: CustomerContactPane,
  [SIDEBAR.EDIT_BILLING_PROFILE_ADDRESS]: ShipToPartySidebar,
  [SIDEBAR.BILLING_ADMIN_PANEL]: BillingAdminPane,
  [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_PAGE_SIDEBAR.INCLUDED_PRODUCTS]: IncludedProductsSidebar,
};

export const BillingProfileDetailsPage = () => {
  const [isFetchInvoiceGroupCountLoading, setIsFetchInvoiceGroupCountLoading] = useState(false);
  const [isDeactivateErrorModalOpen, setIsDeactivateErrorModalOpen] = useState(false);
  const [isDeactivateConfirmModalOpen, setIsDeactivateConfirmModalOpen] = useState(false);
  const [eccDetails, setEccDetails] = useState<ContactInfo[]>([]);

  const { billingProfileId: id, txaId } = useParams<{ billingProfileId: string; txaId: string }>();
  const { closeSidebar, openSidebar } = useSidebar();
  const { notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
  const [isEnabled] = useFeatureToggle();
  const { updateRecentlyViewed } = useRecentlyViewed();
  const { getECCDetails, eccDetailsLoading } = useFetchEccFromContactService(ContactType.CUSTOMER_CONTACT);
  const partnerData = useApplicationContext();

  const selectedPartnerAccountData = partnerData.partnerAccounts.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  );

  const { relatedDomains, isLoading: relatedDomainsLoading } = useGetRelatedDomainsForCurrentTxA();

  useEffect(() => {
    removeContentScrollingHack();
  }, []);

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isBillingProfileNotificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NOTIFICATION_AT_BILLING_PROFILE);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isSwitchBillingProfileOnSamePricingPlanEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_SWITCH_ON_SAME_PRICING_PLAN
  );
  const isBillingProfileArchivalDisabled = isEnabled(STATSIG_FEATURE_GATES.DISABLE_BP_ARCHIVAL);
  const isCustomerContactV3Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);
  const isPaypalSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);
  const isInternalUsageLicensesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_INTERNAL_USAGE_LICENSES_SUPPORT);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isFutureDatedSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS
  );
  const isDefaultFiltersEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DEFAULT_FILTERS);
  const isSoldToAddressArchivalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ARCHIVE_SOLD_TO_ADDRESS);
  const isIULPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_IUL_PHASE_2);
  const isCustomerContactsEditable = isEnabled(STATSIG_FEATURE_GATES.ENABLE_EDIT_ECC);
  const isAchPaymentMethodFFEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ACH_DIRECT_DEBIT);

  const ppcBffGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.PPC_BFF), []);

  const fetchEccDetails = useCallback(async () => {
    if (isInternalUsageLicensesEnabled) {
      try {
        const data = await getECCDetails(id, txaId);
        setEccDetails(data);
      } catch (err) {
        setEccDetails([]);
        if (!notificationSent) {
          const notificationOptions = errorNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withHeader('Some error has occurred')
            .withMessage(failureMessage())
            .build();
          sendErrorNotification(notificationOptions);
          setNotificationSent(true);
        }
      }
    }
  }, [id, isInternalUsageLicensesEnabled, notificationSent, txaId]);

  useEffect(() => {
    fetchEccDetails();
  }, [fetchEccDetails]);

  const handleDeactivateButtonClick = useCallback(async () => {
    try {
      trackButtonClick(AnalyticsButtonLabels.DEACTIVATE_BILLING_PROFILE);
      setIsFetchInvoiceGroupCountLoading(true);
      const res = await getInvoiceGroupCount(id, {
        countType: ['ACTIVE_ENTITLEMENT', 'OPEN_INVOICE', 'OPEN_QUOTE', 'STALE_QUOTE'],
        transactionAccountId: txaId,
      });
      setIsFetchInvoiceGroupCountLoading(false);
      const isAnyActiveElementPresent = Object.values(res).some((value) => value > 0);
      if (isAnyActiveElementPresent) {
        setIsDeactivateErrorModalOpen(true);
        sendAnalyticsEvent(AnalyticsTrackAction.DEACTIVATION_NOT_ALLOWED, AnalyticsTrackSubject.BILLING_PROFILE, {
          transactionAccountId: txaId,
          invoiceGroupId: id,
        });
      } else {
        setIsDeactivateConfirmModalOpen(true);
      }
    } catch (err) {
      setIsFetchInvoiceGroupCountLoading(false);
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Some error has occurred')
          .withMessage(failureMessage())
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      sendAnalyticsEvent(AnalyticsTrackAction.DEACTIVATION_FAILED, AnalyticsTrackSubject.BILLING_PROFILE, {
        transactionAccountId: txaId,
        invoiceGroupId: id,
      });
    }
  }, [id, notificationSent, sendErrorNotification, setNotificationSent, txaId]);

  const { data, loading, error, refetch: refetchBillingProfile } = useQuery<
    FetchInvoiceGroupPpcQuery,
    FetchInvoiceGroupPpcQueryVariables
  >(fetchInvoiceGroupQueryPPC, {
    variables: {
      id,
      txaId,
    },
  });

  const { data: billingAdminsData, loading: billingAdminsLoading } = useQuery<
    FetchBillingAdminsForTransactionAccountPpcQuery,
    FetchBillingAdminsForTransactionAccountPpcQueryVariables
  >(fetchBillingAdminsForTransactionAccountPPC, {
    variables: {
      id: txaId,
      billingAdminOptions: {
        partnerAccountId: selectedPartnerAccountData?.id as string,
        billingAdminIds: [],
      },
    },
    client: ppcBffGraphqlClient,
    displayName: `fetchBillingAdmins`,
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first',
    skip: (!isIULPhase2Enabled && !isBillingProfileNotificationEnabled) || !selectedPartnerAccountData?.id,
  });

  const txaBillingAdminsList = useMemo(() => {
    const billingAdmins = billingAdminsData?.transactionAccount?.billingAdmins?.edges || [];
    return billingAdmins.map((billingAdmin: BillingAdminEdge | null) => ({
      fullName: billingAdmin?.node?.fullName ?? '',
      email: billingAdmin?.node?.email ?? '',
      isPartnerAdmin: billingAdmin?.node?.isPartnerAdmin ?? false,
      picture: billingAdmin?.node.picture ?? '',
      atlassianAccountId: billingAdmin?.node?.atlassianAccountId ?? '',
    }));
  }, [billingAdminsData]);

  const isBillingProfileUsedForInternalUsageLicense = useMemo(() => {
    if (isInternalUsageLicensesEnabled) {
      return getIsBillingProfileUsedForInternalUsageLicense({
        eccDetails,
        shipToPartyId: data?.invoiceGroup?.shipToParty?.id,
        relatedDomains: relatedDomains,
      });
    }
    return false;
  }, [isInternalUsageLicensesEnabled, data, eccDetails, relatedDomains]);

  const shouldRenderForInternalUse = isBillingProfileUsedForInternalUsageLicense && isIULPhase2Enabled;

  useTitle(data?.invoiceGroup?.name ?? '', PPC_PAGE_TITLE.CUSTOMERS);

  useEffect(() => {
    updateRecentlyViewed({
      entityName: getEntityName(RecentlyViewedEntityEnum.BILLING_PROFILE, txaId),
      metadata: {
        id,
        shipToId: data?.invoiceGroup?.shipToParty?.id,
        companyName: data?.invoiceGroup?.shipToParty?.name,
        billingProfileName: data?.invoiceGroup?.name,
      },
    });
  }, [data]);

  if (loading) {
    if (isDesignIteration1Enabled) {
      return <BillingProfileDetailsPageLoader />;
    }

    return <LoadingIndicator />;
  }

  if (error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);

    return <GenericError error={error} />;
  }

  if (!data?.invoiceGroup) {
    const NotFound = withErrorWrapper(ErrorComponents.NotFound);

    return <NotFound />;
  }

  const invoiceGroupDisplayName = data.invoiceGroup?.name;
  const isInvoiceGroupActive = data?.invoiceGroup?.isActive;
  const pageFilterOptions = {
    and: [
      {
        key: 'invoiceGroupId',
        value: id,
      },
    ],
  };

  // filter for quotes tab
  const quotesFilterOptions = {
    and: [...pageFilterOptions.and, ...defaultQuotesFilterOptions.and],
  };

  const quoteTableHiddenTabsFilterOptions = {
    and: [...pageFilterOptions.and, ...hiddenTabsQuoteFilterOptions.and],
  };

  const getInvoiceGroupName = () => {
    if (!invoiceGroupDisplayName || invoiceGroupDisplayName?.toLowerCase() === 'none') {
      return 'Unnamed billing profile';
    }
    return invoiceGroupDisplayName;
  };

  const getInvoiceGroupStatusLozenge = () => {
    if (isInvoiceGroupActive) {
      return <BillingProfileLozenge appearance="success" label="ACTIVE" />;
    } else {
      return <BillingProfileLozenge appearance="removed" label="DEACTIVATED" />;
    }
  };

  const getInternalUsageLicensesLozenge = () => {
    if (isBillingProfileUsedForInternalUsageLicense) {
      return <BillingProfileLozenge appearance="inprogress" label="INTERNAL USE LICENSES" />;
    }
    return null;
  };

  const handleSubmit = async (invoiceGroupRequest: UpdateInvoiceGroupRequest) => {
    try {
      await updateInvoiceGroup(invoiceGroupRequest);
    } catch (err) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update unsuccessful')
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
        closeSidebar();
      }
    }
  };

  const billingProfileFilter = [
    {
      billingGroupId: id,
    },
  ];

  const invoiceContacts = data?.invoiceGroup?.recipients?.length ? data?.invoiceGroup?.recipients : [];

  const onClickEditBillingProfile = () => {
    const openSidebarPayload = {
      sidebarType: SIDEBAR.EDIT_BILLING_PROFILE,
      payload: {
        ...data?.invoiceGroup,
        refetch: () => {
          refetchBillingProfile();
        },
        onSubmit: handleSubmit,
        inflightBannerMessage: DEFAULT_INFLIGHT_BANNER_MESSAGE,
        validDomains: relatedDomains,
        partnerAccountId: selectedPartnerAccountData?.id,
        txaBillingAdmins: txaBillingAdminsList,
        billingAdminsLoading: billingAdminsLoading,
        isBillingProfileUsedForInternalUsageLicense,
      },
      sidebarOptions: {
        isOverlay: true,
        sidebarWidth: '600px',
      },
    } as OpenSidebarPayload;
    openSidebar(openSidebarPayload);
  };

  const getHeaderBackground = () => {
    return <img src={PPCHeader} />;
  };

  function renderOrderHistoryTable() {
    const orderHistoryFilterOptions = [
      {
        key: 'billingGroupId',
        value: id,
      },
    ];

    return (
      <OrderHistoryTable label={'Order history'} filters={orderHistoryFilterOptions} transactionAccountId={txaId} />
    );
  }

  const renderActions = () => {
    if (isInvoiceGroupActive) {
      const billingProfileActions: MoreActionsItems[] = [
        {
          id: 'edit',
          content: 'Edit',
          action: () => onClickEditBillingProfile(),
        },
        {
          id: 'deactivate',
          content: <span className="deactivate-button">Deactivate</span>,
          action: () => {
            handleDeactivateButtonClick();
          },
          predicate: !isBillingProfileArchivalDisabled,
        },
      ];
      return (
        <div className="actions-toolbar">
          <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.UPDATE} resource={ADVOCATE_RESOURCE_TYPE.INVOICE_GROUP}>
            <MoreActionsV2
              actions={billingProfileActions}
              trigger="Actions"
              ariaLabel="menu"
              isLoading={isFetchInvoiceGroupCountLoading || relatedDomainsLoading}
              testId="billing-profile-actions-menu"
            />
          </AdvocateAccessWall>
        </div>
      );
    }
    return null;
  };

  const renderPageHeader = () => {
    if (isEnabled(ENABLE_DESIGN_ITERATION_1)) {
      let pathToCustomer = routePaths.customerAddresses;
      if (data?.invoiceGroup?.shipToParty?.name) {
        const computedRouteUrl = getCompanyNameRoute({ companyName: data.invoiceGroup.shipToParty.name });
        pathToCustomer = `${computedRouteUrl}#billing-profiles`;
      }

      const breadcrumbsItems = [
        {
          title: 'Home',
          href: routePaths.home,
        },
        {
          title: 'Customers',
          href: routePaths.customerAddresses,
        },
        {
          title: data?.invoiceGroup?.shipToParty?.name ?? 'Billing profiles',
          href: pathToCustomer,
        },
        {
          title: getInvoiceGroupName(),
          href: routePaths.billingProfileDetails,
        },
      ];
      return (
        <PageBackgroundV2
          heading={
            <div className="billing-profile-name-wrapper">
              {getInvoiceGroupName()}
              {getInvoiceGroupStatusLozenge()}
              {getInternalUsageLicensesLozenge()}
            </div>
          }
          breadcrumbsProps={{ items: breadcrumbsItems }}
          actions={
            <AdvocateAccessWall
              accessType={ADVOCATE_ACCESS_TYPE.UPDATE}
              resource={ADVOCATE_RESOURCE_TYPE.INVOICE_GROUP}
            >
              {isInvoiceGroupActive && (
                <LoadingButton
                  isLoading={isFetchInvoiceGroupCountLoading}
                  onClick={() => {
                    handleDeactivateButtonClick();
                  }}
                >
                  Deactivate
                </LoadingButton>
              )}
            </AdvocateAccessWall>
          }
          subheading={<CopyableText text={data?.invoiceGroup?.invoiceGroupNumber || data?.invoiceGroup?.id} />}
        />
      );
    }

    return (
      <div className="billing-profile-page-header" data-testid="billing-profile-page-header">
        {getHeaderBackground()}
        <div className="billing-profile-header-group">
          <Button
            iconBefore={<ArrowLeftCircleIcon label="go back" size="medium" primaryColor={N0} secondaryColor={N800A} />}
            appearance="subtle"
            onClick={() => history.back()}
            className="page-back-button"
            testId="page-back-button"
          >
            Back
          </Button>
          <ToolTip content={getInvoiceGroupName()} tag="span" testId="page-heading-tooltip">
            <h1 className="billing-profile-name">
              {getInvoiceGroupName()}
              {getInvoiceGroupStatusLozenge()}
              {getInternalUsageLicensesLozenge()}
            </h1>
          </ToolTip>
        </div>
        {renderActions()}
      </div>
    );
  };

  const renderBillingProfileSummary = (label?: string) => {
    return (
      <BillingProfileSummary
        invoiceGroup={data}
        invoiceGroupName={getInvoiceGroupName()}
        managePaymentMethodsSidebar={SIDEBAR.MANAGE_PAYMENT_METHODS}
        manageBillingProfileNameSidebar={SIDEBAR.EDIT_BILLING_PROFILE_NAME}
        manageBillingProfileAddressSidebar={SIDEBAR.EDIT_BILLING_PROFILE_ADDRESS}
        refetch={refetchBillingProfile}
        submitAction={updateDefaultPaymentMethod}
        {...(isDesignIteration1Enabled ? { label, layoutAppearance: LayoutAppearance.ONE_COLUMN } : {})}
        isPaypalSupportEnabled={isPaypalSupportEnabled}
        isBillingProfileUsedForInternalUsageLicense={isBillingProfileUsedForInternalUsageLicense}
        isFetchingIULDetails={isInternalUsageLicensesEnabled && (eccDetailsLoading || relatedDomainsLoading)}
        isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
        shouldShowACHPaymentMethod={isAchPaymentMethodFFEnabled}
      />
    );
  };

  const invoiceTableColumns = [
    InvoiceTableColumns.INVOICE_NUMBER,
    InvoiceTableColumns.STATUS,
    InvoiceTableColumns.AMOUNT,
    InvoiceTableColumns.ISSUED,
    InvoiceTableColumns.DUE,
    InvoiceTableColumns.PO_NUMBER,
  ];

  return (
    <AppPage
      id={'billing-profile-page'}
      allowedSidebars={allowedSidebars}
      className={
        isDesignIteration1Enabled ? 'billing-profile-details-page-v2' : 'app-page-v2 billing-profile-details-page'
      }
    >
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.PARTNER_BILLING_PROFILE_DETAILS_SCREEN,
          attributes: {},
        }}
      />

      {renderPageHeader()}
      <AppContent>
        {!isDesignIteration1Enabled && renderBillingProfileSummary()}
        {isDeactivateErrorModalOpen && <DeactivateErrorModal onClose={() => setIsDeactivateErrorModalOpen(false)} />}
        {isDeactivateConfirmModalOpen && (
          <DeactivateConfirmModal
            invoiceGroupId={id}
            transactionAccountId={txaId}
            refetchBillingProfile={refetchBillingProfile}
            onClose={() => setIsDeactivateConfirmModalOpen(false)}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        )}
        <div className={isDesignIteration1Enabled ? '' : 'billing-profile-details-container'}>
          <div data-testid="billing-profile-details-tab">
            <PageTabs showAllTab={false}>
              <SubscriptionTableFlatContainer
                transactionAccountId={txaId}
                filterOptions={pageFilterOptions.and}
                label="Subscriptions"
                isFreeTabEnabled={false}
                isRowSelectionRequired={true}
                isBulkTransferButtonRequired={true}
                {...(isDesignIteration1Enabled && { shouldHideCustomiseView: true, shouldHideTabs: true })}
                isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
                shouldAllowBillingProfileTransferBasedOnPricingEligibility={
                  isSwitchBillingProfileOnSamePricingPlanEnabled
                }
                allowedPricingEligibilityForBillingProfileTransfer={[
                  get(data, 'invoiceGroup.shipToParty.shipToPriceEligibility', COMMERCIAL_PRICE_ELIGIBILITY),
                ]}
                isInternalUsageLicensesEnabled={isInternalUsageLicensesEnabled}
                isSupportForExternallyBilledSubscriptionsEnabled={true}
                isTWCPhase1Enabled={isTWCPhase1Enabled}
                isFutureDatedSubscriptionsEnabled={isFutureDatedSubscriptionsEnabled}
              />
              <QuoteTableContainer
                transactionAccountId={txaId}
                label="Quotes"
                filterOptions={
                  isDesignIteration1Enabled && !isDefaultFiltersEnabled
                    ? quoteTableHiddenTabsFilterOptions
                    : quotesFilterOptions
                }
                shouldDisableRequestQuoteCta={!isInvoiceGroupActive}
              />
              {!isCustomerContactV3Enabled && (
                <InvoiceContactsTab
                  label={isDesignIteration1Enabled ? INVOICE_CONTACTS : ADDITIONAL_INVOICE_RECIPIENTS}
                  shipToAddress={data?.invoiceGroup?.shipToParty}
                  invoiceGroupId={id}
                  transactionAccountId={txaId}
                  loading={loading || relatedDomainsLoading}
                  onComplete={refetchBillingProfile}
                  invoiceContacts={invoiceContacts as string[]}
                  validDomains={relatedDomains}
                />
              )}
              {(isCustomerContactV3Enabled || isDesignIteration1Enabled) && (
                <CustomerContactsTab
                  label={shouldRenderForInternalUse ? 'Contacts' : 'Customer contacts'}
                  invoiceGroupId={id}
                  version={isDesignIteration1Enabled ? 2 : 1}
                  customerContactsSidebarV2={SIDEBAR.CUSTOMER_CONTACT_PANEL}
                  customerContactType={ContactType.CUSTOMER_CONTACT}
                  customerName={getInvoiceGroupName()}
                  showTableActionColumn
                  shouldDisableAddEditActions={!isInvoiceGroupActive}
                  shouldRenderForInternalUse={shouldRenderForInternalUse}
                  transactionAccountBillingAdmins={txaBillingAdminsList}
                  transactionAccountBillingAdminsLoading={billingAdminsLoading}
                  isCustomerContactsEditable={isCustomerContactsEditable}
                  validPartnerDomains={relatedDomains}
                />
              )}
              {!shouldRenderForInternalUse && (isCustomerContactV3Enabled || isDesignIteration1Enabled) && (
                <CustomerContactsTab
                  label="Billing admins"
                  invoiceGroupId={id}
                  version={isDesignIteration1Enabled ? 2 : 1}
                  customerContactsSidebarV2={SIDEBAR.BILLING_ADMIN_PANEL}
                  customerContactType={ContactType.BILLING_CONTACT}
                  transactionAccountBillingAdmins={txaBillingAdminsList}
                  transactionAccountBillingAdminsLoading={billingAdminsLoading}
                  showTableActionColumn
                  shouldDisableAddEditActions={!isInvoiceGroupActive}
                />
              )}
              <InvoiceTable
                label="Invoices"
                transactionAccountId={txaId}
                id={txaId}
                filters={billingProfileFilter}
                columns={invoiceTableColumns}
                {...(isDesignIteration1Enabled && { shouldHideCustomiseView: true })}
              />
              {isDesignIteration1Enabled && (
                <InvoiceTable
                  label="Refunds"
                  transactionAccountId={txaId}
                  id={txaId}
                  filters={billingProfileFilter}
                  columns={invoiceTableColumns}
                  shouldHideCustomiseView={true}
                  selectedInterfaceKey={refundedTabNameKey}
                />
              )}
              {renderOrderHistoryTable()}
              {isDesignIteration1Enabled && renderBillingProfileSummary('Details')}
            </PageTabs>
          </div>
        </div>
      </AppContent>
    </AppPage>
  );
};
